<script setup>
const emit = defineEmits(['pageChange', 'routeChange'])

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    required: true,
  },
})

const goToPage = (page) => {
  // Emit event for parent component to handle pagination
  emit('pageChange', page)
}

const goToRoute = (id) => {
  // Emit event for parent component to handle pagination
  emit('routeChange', id)
}
</script>

<template>
  <div class="w-full">
        <div
          class="overflow-hidden p-4 mx-auto w-full text-gray-900 bg-white shadow-sm sm:p-6 dark:text-gray-200 sm:px-6 lg:px-8 dark:bg-gray-800 dark:shadow-gray-700 sm:rounded-lg"
        >
          <div class="overflow-x-auto w-full" style="-webkit-overflow-scrolling: touch;">
              <table class="min-w-full divide-y divide-gray-200 table-fixed md:table-auto">
                <thead class="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th
                      v-for="column in columns"
                      :key="column.key"
                      class="px-3 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase sm:px-6 dark:text-gray-300"
                      :style="column.width ? { width: column.width } : {}"
                    >
                      {{ column.label }}
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700"
                >
                  <tr
                    v-for="(item, index) in items"
                    :key="index"
                    @click="goToRoute(item.uuid ?? item.id)"
                    class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td
                      v-for="column in columns"
                      :key="column.key"
                      class="overflow-hidden px-3 py-3 text-sm whitespace-nowrap sm:px-6 sm:py-4 text-ellipsis"
                      :class="column.class"
                    >
                      <slot :name="column.key" :item="item" :column="column">
                        {{ column.format ? column.format(item) : item[column.key] }}
                      </slot>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="flex flex-col gap-4 justify-between items-center mt-4 sm:flex-row">
              <div class="text-xs text-center text-gray-700 sm:text-sm dark:text-gray-300 sm:text-left">
                Showing {{ pagination.from }} to {{ pagination.to }} of
                {{ pagination.total }} results
              </div>
              <div class="flex space-x-2">
                <button
                  v-if="pagination.current_page > 1"
                  @click="goToPage(pagination.current_page - 1)"
                  class="px-3 py-1 text-sm text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 min-w-[80px] flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="mr-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Prev
                </button>
                <button
                  v-if="pagination.current_page < pagination.last_page"
                  @click="goToPage(pagination.current_page + 1)"
                  class="px-3 py-1 text-sm text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 min-w-[80px] flex items-center justify-center"
                >
                  Next
                  <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
</template>
